import { Navigate, Outlet, Route, Routes, useParams } from "react-router-dom";

import { BundleProvider, useBundleState } from "./context/BundleContext";
import { ErrorBoundary } from "./components/shell/ErrorBoundary";
import { Nav } from "./components/shell/Nav";
import { RouteSkeleton } from "./components/shell/RouteSkeleton";
import { BundleErrorState } from "./components/shell/BundleErrorState";
import { CaseOverviewRoute } from "./routes/CaseOverviewRoute";
import { ReplayRoute } from "./routes/ReplayRoute";
import { ReceiptRoute } from "./routes/ReceiptRoute";
import { NotFoundRoute } from "./routes/NotFoundRoute";
import { baseUrlForCase, DEFAULT_CASE_ID } from "./lib/cases";

/** Nav plus the shared loading/error/ready shell for whichever case's bundle is currently loading under this provider. */
function CaseBundleShell() {
  const state = useBundleState();
  const caseId = state.status === "ready" ? state.bundle.overview.caseId : undefined;

  return (
    <div className="flex min-h-dvh flex-col">
      <Nav caseId={caseId} />
      <main id="main" className="flex-1">
        {state.status === "loading" && <RouteSkeleton />}
        {state.status === "error" && <BundleErrorState error={state.error} />}
        {state.status === "ready" && <Outlet />}
      </main>
    </div>
  );
}

/**
 * Resolves `:caseId` from the URL to a bundle location (see
 * `lib/cases.ts`) and mounts a fresh `BundleProvider` for it. `key={caseId}`
 * is load-bearing: switching cases must remount the provider and start a
 * new fetch, never reuse the previous case's already-loaded state.
 *
 * An unrecognised `caseId` is a real 404 (`NotFoundRoute`), not an
 * `ARTIFACT_MISSING` bundle error -- those are different facts: one means
 * "this case doesn't exist in the viewer", the other means "this case's
 * bundle failed to load".
 */
function CaseRoute() {
  const { caseId } = useParams<{ caseId: string }>();
  const baseUrl = caseId !== undefined ? baseUrlForCase(caseId) : undefined;

  if (caseId === undefined || baseUrl === undefined) {
    return (
      <div className="flex min-h-dvh flex-col">
        <Nav caseId={undefined} />
        <main id="main" className="flex-1">
          <NotFoundRoute />
        </main>
      </div>
    );
  }

  return (
    <BundleProvider key={caseId} baseUrl={baseUrl}>
      <CaseBundleShell />
    </BundleProvider>
  );
}

export function App() {
  return (
    <ErrorBoundary>
      <Routes>
        <Route path="/" element={<Navigate to={`/cases/${DEFAULT_CASE_ID}`} replace />} />
        <Route path="/cases/:caseId" element={<CaseRoute />}>
          <Route index element={<CaseOverviewRoute />} />
          <Route path="replay" element={<ReplayRoute />} />
          <Route path="receipt" element={<ReceiptRoute />} />
        </Route>
        <Route path="*" element={<NotFoundRoute />} />
      </Routes>
    </ErrorBoundary>
  );
}
